/**
 * Слой авторизации веба поверх модуля identity бэкенда (ПОРТ-16, ПОРТ-21, ПОРТ-22, БЕЗ-10):
 * `POST /auth/refresh`, `GET /me`, `GET /me/clinics`, `PUT /me/clinics/current`, `POST /auth/mfa/*`,
 * `POST /auth/unlock/*`, `POST /auth/logout` (backend/openapi.json).
 *
 * Хранение: access-токен — только в памяти вкладки; refresh-токен — httpOnly Secure SameSite=Strict
 * cookie, которую ставит сервер (JavaScript её не читает, XSS не может её украсть). Поэтому веб
 * не передаёт refreshToken в теле запросов — сервер берёт его из cookie.
 */
import { ApiError } from '@dodoc/api-client';
import type { AppLocale } from '@/shared/format/locale-data';
import { DEFAULT_TIME_ZONE, isValidTimeZone } from '@/shared/format/date';
import { apiErrorFromResponse, networkError } from '../api/http';
import { isClinicRole, isStaffRole } from './roles';
import type { AccessToken, AuthResult, ClinicKind, ClinicStatus, LockPolicy, MfaProof, MfaState, OtpChallenge, OtpChannel, SessionMembership, SessionUser, TotpSetup } from './types';

export interface AuthApi {
  /** Новые токены по refresh-cookie; null — активной сессии нет. 423 IDENTITY_SESSION_LOCKED — сессия заблокирована. */
  refresh(): Promise<AuthResult | null>;
  /** Текущий пользователь, его глобальные роли, состояние MFA и членства в клиниках. */
  me(accessToken: string): Promise<SessionUser>;
  /** Запомнить выбранную клинику на сервере (переключатель, ПОРТ-16). */
  rememberClinic(clinicId: string, accessToken: string): Promise<void>;
  /** Второй фактор → новый access-токен с mfa=true (MFA_REQUIRED, ПОРТ-21). */
  verifyMfa(proof: MfaProof, accessToken: string): Promise<AccessToken>;
  /** Начать подключение приложения-аутентификатора: секрет и otpauth-ссылка. */
  setupTotp(accessToken: string): Promise<TotpSetup>;
  /** Включить TOTP первым кодом: новый токен и одноразовые коды восстановления (показываются один раз). */
  enableTotp(code: string, accessToken: string): Promise<{ token: AccessToken; recoveryCodes: string[] }>;
  /** Код разблокировки на номер владельца сессии (SMS или WhatsApp). */
  requestUnlockCode(channel: OtpChannel): Promise<OtpChallenge>;
  /** Разблокировать сессию PIN-кодом устройства. */
  unlockWithPin(pin: string): Promise<AuthResult>;
  /** Разблокировать сессию кодом из SMS или WhatsApp. */
  unlockWithCode(challengeId: string, code: string): Promise<AuthResult>;
  /** Выход: сервер отзывает сессию и удаляет cookie. */
  logout(accessToken: string | null): Promise<void>;
}

export const AUTH_ENDPOINTS = {
  refresh: '/api/v1/auth/refresh',
  me: '/api/v1/me',
  myClinics: '/api/v1/me/clinics',
  currentClinic: '/api/v1/me/clinics/current',
  mfaVerify: '/api/v1/auth/mfa/verify',
  totpSetup: '/api/v1/auth/mfa/totp/setup',
  totpEnable: '/api/v1/auth/mfa/totp/enable',
  unlockPin: '/api/v1/auth/unlock/pin',
  unlockCodeRequest: '/api/v1/auth/unlock/otp/request',
  unlockCodeVerify: '/api/v1/auth/unlock/otp/verify',
  logout: '/api/v1/auth/logout',
} as const;

/** Блокировка веб-сессии по бездействию (сервер: 423). */
export const SESSION_LOCKED_CODE = 'IDENTITY_SESSION_LOCKED';
/** Блокировка после 15 минут бездействия (НФТ MVP-ТЗ скрайба, ADR-0017); сервер присылает своё значение. */
export const DEFAULT_IDLE_MINUTES = 15;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(record: Record<string, unknown>, key: string, fallback = ''): string {
  const value = record[key];
  return typeof value === 'string' ? value : fallback;
}

function invalidResponse(what: string): ApiError {
  return new ApiError(502, { error: { code: 'UNKNOWN', message: `Invalid ${what} response`, params: {}, requestId: null } });
}

/** SessionTokensDto → AccessToken (refresh-токен веба остаётся в cookie). */
export function parseAccessToken(value: unknown): AccessToken {
  if (!isRecord(value) || typeof value.accessToken !== 'string' || value.accessToken === '') throw invalidResponse('token');
  const expires =
    typeof value.accessExpiresAt === 'string' && !Number.isNaN(Date.parse(value.accessExpiresAt))
      ? value.accessExpiresAt
      : new Date(Date.now() + 15 * 60 * 1000).toISOString();
  return { accessToken: value.accessToken, accessExpiresAt: expires };
}

export function parseMfaState(value: unknown): MfaState {
  const mfa = isRecord(value) ? value : {};
  return { required: mfa.required === true, enrolled: mfa.enrolled === true, passed: mfa.passed === true };
}

function parseLockPolicy(value: unknown): LockPolicy | null {
  if (!isRecord(value)) return null;
  const minutes = typeof value.webIdleMinutes === 'number' && value.webIdleMinutes > 0 ? value.webIdleMinutes : DEFAULT_IDLE_MINUTES;
  return { idleMinutes: minutes, pinEnabled: value.pinEnabled === true };
}

/** AuthResultDto (обновление, разблокировка) → AuthResult. Статус registration_required для сессии невозможен. */
export function parseAuthResult(value: unknown): AuthResult {
  if (!isRecord(value) || value.status !== 'authenticated') throw invalidResponse('auth');
  return { token: parseAccessToken(value.session), mfa: parseMfaState(value.mfa), lockPolicy: parseLockPolicy(value.lockPolicy) };
}

const CLINIC_KINDS: readonly ClinicKind[] = ['clinic', 'personal', 'demo'];
const CLINIC_STATUSES: readonly ClinicStatus[] = ['onboarding', 'active', 'suspended', 'terminated'];

function parseMembership(value: unknown): SessionMembership | null {
  if (!isRecord(value) || typeof value.clinicId !== 'string') return null;
  const kind = CLINIC_KINDS.includes(value.kind as ClinicKind) ? (value.kind as ClinicKind) : 'clinic';
  const status = CLINIC_STATUSES.includes(value.status as ClinicStatus) ? (value.status as ClinicStatus) : 'active';
  return {
    clinicId: value.clinicId,
    clinicName: stringField(value, 'name'),
    kind,
    status,
    isDemo: value.isDemo === true,
    roles: Array.isArray(value.roles) ? value.roles.filter(isClinicRole) : [],
    branchIds: Array.isArray(value.branchIds) ? value.branchIds.filter((id): id is string => typeof id === 'string') : [],
    // Пояс клиники из /me/clinics (первый активный филиал): время приёма и подписи — в нём, а не в поясе по умолчанию.
    timeZone: typeof value.timeZone === 'string' && isValidTimeZone(value.timeZone) ? value.timeZone : DEFAULT_TIME_ZONE,
  };
}

/**
 * GET /me (MeWithMfaDto) + GET /me/clinics (MyClinicsDto) → SessionUser.
 * Неизвестные роли отбрасываются; расторгнутые клиники не показываются в переключателе.
 */
export function parseSessionUser(me: unknown, clinics: unknown): SessionUser {
  if (!isRecord(me) || typeof me.id !== 'string') throw invalidResponse('/me');
  const clinicList = isRecord(clinics) && Array.isArray(clinics.clinics) ? clinics.clinics : [];
  const memberships = clinicList.map(parseMembership).filter((item): item is SessionMembership => item !== null && item.status !== 'terminated');
  const serverCurrent = isRecord(clinics) && typeof clinics.currentClinicId === 'string' ? clinics.currentClinicId : null;
  const lastClinic = serverCurrent ?? (typeof me.lastClinicId === 'string' ? me.lastClinicId : null);
  return {
    id: me.id,
    fullName: stringField(me, 'fullName'),
    phone: stringField(me, 'phone'),
    locale: me.locale === 'kk' ? 'kk' : 'ru',
    globalRoles: Array.isArray(me.globalRoles) ? me.globalRoles.filter(isStaffRole) : [],
    mfa: parseMfaState(me.mfa),
    lastClinicId: lastClinic,
    memberships,
  };
}

function parseTotpSetup(value: unknown): TotpSetup {
  if (!isRecord(value) || typeof value.secret !== 'string' || typeof value.otpauthUri !== 'string') throw invalidResponse('totp');
  return { secret: value.secret, otpauthUri: value.otpauthUri, issuer: stringField(value, 'issuer'), account: stringField(value, 'account') };
}

function parseChallenge(value: unknown): OtpChallenge {
  if (!isRecord(value) || typeof value.challengeId !== 'string') throw invalidResponse('otp');
  return {
    challengeId: value.challengeId,
    channel: value.channel === 'whatsapp' ? 'whatsapp' : 'sms',
    expiresAt: stringField(value, 'expiresAt'),
    resendAfterSeconds: typeof value.resendAfterSeconds === 'number' ? value.resendAfterSeconds : 60,
    phoneMasked: stringField(value, 'phoneMasked'),
    codeLength: typeof value.codeLength === 'number' ? value.codeLength : 6,
  };
}

export interface HttpAuthApiOptions {
  baseUrl: string;
  getLocale: () => AppLocale;
  fetch?: (request: Request) => Promise<Response>;
}

/** Реализация AuthApi поверх HTTP. */
export class HttpAuthApi implements AuthApi {
  constructor(private readonly options: HttpAuthApiOptions) {}

  private async send(method: 'GET' | 'POST' | 'PUT', path: string, init: { token?: string | null; body?: unknown } = {}): Promise<Response> {
    const headers = new Headers({ Accept: 'application/json', 'X-Locale': this.options.getLocale() });
    if (init.token) headers.set('Authorization', `Bearer ${init.token}`);
    if (init.body !== undefined) headers.set('Content-Type', 'application/json');
    const request = new Request(new URL(path, this.options.baseUrl), {
      method,
      headers,
      credentials: 'include',
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    try {
      return await (this.options.fetch ?? ((input: Request) => fetch(input)))(request);
    } catch {
      throw networkError();
    }
  }

  private async json(method: 'GET' | 'POST' | 'PUT', path: string, init: { token?: string | null; body?: unknown } = {}): Promise<unknown> {
    const response = await this.send(method, path, init);
    if (!response.ok) throw await apiErrorFromResponse(response);
    return response.status === 204 ? null : response.json();
  }

  async refresh(): Promise<AuthResult | null> {
    const response = await this.send('POST', AUTH_ENDPOINTS.refresh, { body: {} });
    if (response.status === 401) return null;
    if (!response.ok) throw await apiErrorFromResponse(response);
    return parseAuthResult(await response.json());
  }

  async me(accessToken: string): Promise<SessionUser> {
    const [me, clinics] = await Promise.all([
      this.json('GET', AUTH_ENDPOINTS.me, { token: accessToken }),
      this.json('GET', AUTH_ENDPOINTS.myClinics, { token: accessToken }),
    ]);
    return parseSessionUser(me, clinics);
  }

  async rememberClinic(clinicId: string, accessToken: string): Promise<void> {
    await this.json('PUT', AUTH_ENDPOINTS.currentClinic, { token: accessToken, body: { clinicId } });
  }

  async verifyMfa(proof: MfaProof, accessToken: string): Promise<AccessToken> {
    const result = await this.json('POST', AUTH_ENDPOINTS.mfaVerify, { token: accessToken, body: proof });
    return parseAccessToken(isRecord(result) ? result.session : null);
  }

  async setupTotp(accessToken: string): Promise<TotpSetup> {
    return parseTotpSetup(await this.json('POST', AUTH_ENDPOINTS.totpSetup, { token: accessToken, body: {} }));
  }

  async enableTotp(code: string, accessToken: string): Promise<{ token: AccessToken; recoveryCodes: string[] }> {
    const result = await this.json('POST', AUTH_ENDPOINTS.totpEnable, { token: accessToken, body: { code } });
    if (!isRecord(result)) throw invalidResponse('totp enable');
    const codes = Array.isArray(result.recoveryCodes) ? result.recoveryCodes.filter((item): item is string => typeof item === 'string') : [];
    return { token: parseAccessToken(result.session), recoveryCodes: codes };
  }

  async requestUnlockCode(channel: OtpChannel): Promise<OtpChallenge> {
    return parseChallenge(await this.json('POST', AUTH_ENDPOINTS.unlockCodeRequest, { body: { channel } }));
  }

  async unlockWithPin(pin: string): Promise<AuthResult> {
    return parseAuthResult(await this.json('POST', AUTH_ENDPOINTS.unlockPin, { body: { pin } }));
  }

  async unlockWithCode(challengeId: string, code: string): Promise<AuthResult> {
    return parseAuthResult(await this.json('POST', AUTH_ENDPOINTS.unlockCodeVerify, { body: { challengeId, code } }));
  }

  async logout(accessToken: string | null): Promise<void> {
    const response = await this.send('POST', AUTH_ENDPOINTS.logout, { token: accessToken, body: {} });
    if (!response.ok && response.status !== 401) throw await apiErrorFromResponse(response);
  }
}
